import { BrandChip } from "./BrandChip";
import { StarRating } from "./StarRating";
import { formatAddress, SERVICE_LABELS, statusBadge } from "../lib/format";
import type { Location } from "../lib/types";

interface Props {
  loc: Location;
  selected?: boolean;
  onClick: () => void;
  onView: (loc: Location) => void;
}

export function LocationCard({ loc, selected, onClick, onView }: Props) {
  const services = Object.entries(loc.services ?? {})
    .filter(([k, v]) => v === true && SERVICE_LABELS[k])
    .map(([k]) => SERVICE_LABELS[k]);
  const status = statusBadge(loc);

  return (
    <article
      onClick={onClick}
      className={`result-card ${selected ? "is-selected" : ""}`}
    >
      <div className="flex items-start gap-3">
        <BrandChip loc={loc} />
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-wider text-slate-500">
                {loc.partner_chain?.display_name ?? "MoneyGram"}
              </div>
              <h3 className="font-display font-bold text-base text-slate-900 truncate">
                {loc.name}
              </h3>
              <span className="text-sm text-slate-700 hover:text-slate-900 underline underline-offset-2 break-words">
                {formatAddress(loc.address)}
              </span>
            </div>
            <button
              type="button"
              aria-label="Open details"
              onClick={(e) => {
                e.stopPropagation();
                onView(loc);
              }}
              className="text-slate-400 hover:text-slate-700 self-start text-2xl leading-none"
            >
              ›
            </button>
          </div>

          <div className="mt-2 flex flex-wrap gap-1.5">
            {loc.is_top_rated && <span className="pill pill-amber">Top rated</span>}
            {typeof loc.distance_miles === "number" &&
              loc.distance_miles < 2 && (
                <span className="pill pill-success">Near you</span>
              )}
            {typeof loc.avg_wait_minutes === "number" &&
              loc.avg_wait_minutes <= 5 && (
                <span className="pill pill-info">Fast</span>
              )}
          </div>

          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
            {typeof loc.distance_miles === "number" && (
              <span>{loc.distance_miles.toFixed(1)} mi</span>
            )}
            {typeof loc.avg_wait_minutes === "number" && (
              <span>~{loc.avg_wait_minutes} min wait</span>
            )}
            <span className={status.cls.includes("emerald") ? "text-emerald-700" : "text-rose-700"}>
              {status.label}
            </span>
            <span className="font-mono text-slate-400">
              {loc.location_id}
            </span>
          </div>

          {services.length > 0 && (
            <div className="mt-2 text-xs text-slate-500 truncate">
              {services.slice(0, 4).join(" · ")}
              {services.length > 4 && ` +${services.length - 4}`}
            </div>
          )}

          {(loc.rating ?? 0) > 0 && (
            <div className="mt-2">
              <StarRating value={loc.rating ?? 0} count={loc.rating_count ?? 0} />
            </div>
          )}
        </div>
      </div>
    </article>
  );
}
